import {
    getPostRequestCaptureConfig,
    shouldEnableFormTracking,
} from '../../../config/SiteConfigManager';
import { logger } from '../../../utils/core/logger';
import { matchesUrlPattern } from '../../../utils/web/urlUtils';
import type { FormDetectionContext } from '../types';

// Minimum number of visible input fields to consider a page as having a "formless form"
const MIN_INPUT_FIELDS = 2;

// Domains whose POSTs should be ignored by the interceptor (analytics, tracking pixels, etc.)
const IGNORED_POST_DOMAINS = [
    'ads.linkedin.com',
    'px.ads.linkedin.com',
    'analytics.google.com',
    'google-analytics.com',
    'googleads.g.doubleclick.net',
    'facebook.com/tr',
    'connect.facebook.net',
    'bat.bing.com',
    'api.segment.io',
    'cdn.segment.com',
    'api-js.mixpanel.com',
    'sentry.io',
    'app.posthog.com',
    'us.i.posthog.com',
    'eu.i.posthog.com',
];

function isTrackingPixelUrl(url: string): boolean {
    try {
        const hostname = new URL(url).hostname;
        return IGNORED_POST_DOMAINS.some(
            (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
        );
    } catch {
        return false;
    }
}

// Selectors for submit-like buttons outside of <form> elements
const SUBMIT_BUTTON_SELECTORS = [
    'button[type="submit"]',
    'button[type="button"]',
    'button:not([type])',
    'input[type="submit"]',
    'a[role="button"]',
].join(', ');

// Keywords in button text that indicate a submit action
const SUBMIT_KEYWORDS = [
    // English
    'submit',
    'send',
    'sign up',
    'register',
    'next step',
    'continue',
    'get started',
    'create account',
    // French
    'envoyer',
    'soumettre',
    'inscription',
    'inscrire',
    'étape suivante',
    'continuer',
    'commencer',
    'créer',
    // Spanish
    'enviar',
    'registrar',
    'siguiente',
    'continuar',
    'comenzar',
    'crear cuenta',
    // German
    'absenden',
    'registrieren',
    'weiter',
    'fortfahren',
    'konto erstellen',
];

/**
 * Strategy 5: Network Observer Detection
 *
 * Detects form submissions on pages that use input fields WITHOUT a wrapping <form> element.
 * Common in Angular, React, and Vue SPAs that handle submission via JavaScript.
 *
 * Detection approach:
 * 1. On eligible pages (CTA destinations or form_tracking_pages), scan for orphan inputs
 * 2. Find submit-like buttons near those inputs
 * 3. On button click, intercept the next XHR/fetch POST as the submission
 * 4. Track success/failure based on DOM changes after the POST
 *
 * Like ThankYouPageStrategy, this is NOT applied per-form. It's initialized by the manager
 * on pages that match form tracking patterns but have no <form> elements.
 */
export class NetworkObserverStrategy {
    name = 'network_observer';
    private isActive = false;
    private trackedButtons = new WeakSet<Element>();
    private pendingSubmission: {
        buttonText: string;
        inputCount: number;
        timestamp: number;
    } | null = null;
    private mutationObserver: MutationObserver | null = null;
    private performanceObserver: PerformanceObserver | null = null;
    /**
     * Check if this page has formless inputs worth tracking.
     * Called by FormDetectionManager when no <form> elements are found.
     */
    checkAndActivate(context: FormDetectionContext): boolean {
        if (this.isActive) return true;

        // Must be on a form-tracking-eligible page
        if (!shouldEnableFormTracking(context.domain)) {
            // Also check post_request_capture page patterns as a fallback
            const postCaptureConfig = getPostRequestCaptureConfig(context.domain);
            const isPostCapturePage = postCaptureConfig?.pagePatterns?.some((pattern) =>
                matchesUrlPattern(window.location.href, pattern)
            );

            if (!isPostCapturePage) {
                return false;
            }
        }

        // Check for orphan inputs (inputs not inside a <form>)
        const orphanInputs = this.findOrphanInputs();
        if (orphanInputs.length < MIN_INPUT_FIELDS) {
            logger.debug('📄 NetworkObserverStrategy: not enough orphan inputs', {
                found: orphanInputs.length,
                minimum: MIN_INPUT_FIELDS,
            });
            return false;
        }

        // Must have an email input — this is the strongest signal of a lead capture form.
        // Some SPAs (e.g. PayFit welcome funnel) render email fields as type="text" but with
        // autocomplete="email" or inputmode="email" — treat those as email inputs too.
        const hasEmailInput = orphanInputs.some(
            (el) =>
                el instanceof HTMLInputElement &&
                (el.type === 'email' || el.autocomplete === 'email' || el.inputMode === 'email')
        );
        if (!hasEmailInput) {
            logger.debug('📄 NetworkObserverStrategy: no email input found among orphan inputs');
            return false;
        }

        // Find submit-like buttons
        const submitButton = this.findSubmitButton(orphanInputs);
        if (!submitButton) {
            logger.debug('📄 NetworkObserverStrategy: no submit button found near inputs');
            return false;
        }

        logger.debug('📄 NetworkObserverStrategy: activating on formless page', {
            orphanInputCount: orphanInputs.length,
            submitButtonText: submitButton.textContent?.trim(),
            url: window.location.href,
        });

        this.attachButtonListener(submitButton, orphanInputs.length, context);
        this.isActive = true;

        // Watch for dynamically added buttons (SPA multi-step forms)
        this.observeForNewButtons(context);

        return true;
    }

    private findOrphanInputs(): HTMLElement[] {
        const allInputs = document.querySelectorAll('input, select, textarea');
        const orphans: HTMLElement[] = [];

        allInputs.forEach((input) => {
            const el = input as HTMLElement;
            // Skip hidden inputs and inputs inside <form> elements
            if (el.closest('form')) return;
            if (el instanceof HTMLInputElement && (el.type === 'hidden' || el.type === 'search'))
                return;
            if (el.offsetParent === null) return; // hidden

            orphans.push(el);
        });

        return orphans;
    }

    private findSubmitButton(orphanInputs: HTMLElement[]): Element | null {
        // Find a common ancestor of the orphan inputs
        const ancestors = new Set<Element>();
        for (const input of orphanInputs) {
            let el: Element | null = input.parentElement;
            while (el && el !== document.body) {
                ancestors.add(el);
                el = el.parentElement;
            }
        }

        // Look for submit-like buttons within those ancestors
        const buttons = document.querySelectorAll(SUBMIT_BUTTON_SELECTORS);
        for (const button of buttons) {
            if (button.closest('form')) continue; // Skip buttons inside forms
            if ((button as HTMLElement).offsetParent === null) continue; // Skip hidden buttons

            const buttonText = button.textContent?.trim().toLowerCase() || '';
            const isSubmitLike = SUBMIT_KEYWORDS.some((kw) => buttonText.includes(kw));

            if (!isSubmitLike) continue;

            // Check if button shares a common ancestor with the inputs
            let el: Element | null = button.parentElement;
            while (el && el !== document.body) {
                if (ancestors.has(el)) {
                    return button;
                }
                el = el.parentElement;
            }
        }

        return null;
    }

    private attachButtonListener(
        button: Element,
        inputCount: number,
        context: FormDetectionContext
    ): void {
        if (this.trackedButtons.has(button)) return;
        this.trackedButtons.add(button);

        const buttonText = button.textContent?.trim() || '';

        button.addEventListener('click', () => {
            logger.debug('📄 NetworkObserverStrategy: submit button clicked', {
                buttonText,
                inputCount,
            });

            // Record the pending submission — we'll watch for the XHR/navigation result
            this.pendingSubmission = {
                buttonText,
                inputCount,
                timestamp: Date.now(),
            };

            // Passively observe network requests to detect the submission POST
            this.observeNetworkAfterClick(context, inputCount);
        });

        logger.debug('📄 NetworkObserverStrategy: attached listener to button', {
            buttonText,
        });
    }

    /**
     * Observe network requests passively using PerformanceObserver.
     * Unlike monkey-patching fetch/XHR, this cannot break third-party scripts.
     * Limitation: PerformanceObserver cannot see HTTP method or status code,
     * so we treat any non-tracking-pixel request as a potential form POST.
     * The fallback DOM check (5s timeout) handles the case where no POST is detected.
     */
    private observeNetworkAfterClick(context: FormDetectionContext, inputCount: number): void {
        // Clean up any previous observer
        if (this.performanceObserver) {
            this.performanceObserver.disconnect();
        }

        const clickTimestamp = Date.now();

        this.performanceObserver = new PerformanceObserver((list) => {
            if (!this.pendingSubmission) return;

            for (const entry of list.getEntries()) {
                const resourceEntry = entry as PerformanceResourceTiming;
                const url = resourceEntry.name;

                // Skip tracking pixels and static assets
                if (isTrackingPixelUrl(url)) continue;
                if (/\.(js|css|png|jpg|gif|svg|woff|ico)(\?|$)/i.test(url)) continue;

                // Only consider fetch/XHR requests (not images, scripts, etc.)
                const type = resourceEntry.initiatorType;
                if (type !== 'fetch' && type !== 'xmlhttprequest') continue;

                // Ignore requests that started before (or too soon after) the click —
                // these are likely pre-existing background requests, not form POSTs
                const requestStart = resourceEntry.startTime + performance.timeOrigin;
                if (requestStart < clickTimestamp + 100) continue;

                logger.debug(
                    '📄 NetworkObserverStrategy: detected network request after button click',
                    {
                        url: url.substring(0, 200),
                        initiatorType: type,
                        inputCount,
                    }
                );

                // Found a real API call after button click — track it
                this.pendingSubmission = null;
                this.performanceObserver?.disconnect();

                context.onFormSubmit({
                    formId: 'network_observer',
                    // Method label, not a form identity — stays out of the
                    // form_id_whitelist, which could never contain it (IX-4411).
                    applyFormIdWhitelist: false,
                    pageUrl: context.currentUrl,
                    formAction: url,
                    formMethod: 'POST',
                    fieldCount: inputCount,
                    detectionMethod: this.name,
                    status: 'success',
                    indicators: ['network_observer_detected', `initiator_${type}`],
                });

                return;
            }
        });

        this.performanceObserver.observe({ type: 'resource', buffered: false });

        // Auto-disconnect after 10s to avoid leaking observers
        setTimeout(() => {
            this.performanceObserver?.disconnect();
            this.pendingSubmission = null;
        }, 10000);
    }

    private observeForNewButtons(context: FormDetectionContext): void {
        this.mutationObserver = new MutationObserver(() => {
            const orphanInputs = this.findOrphanInputs();
            if (orphanInputs.length < MIN_INPUT_FIELDS) return;

            const button = this.findSubmitButton(orphanInputs);
            if (button && !this.trackedButtons.has(button)) {
                this.attachButtonListener(button, orphanInputs.length, context);
            }
        });

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    cleanup(): void {
        this.isActive = false;
        this.pendingSubmission = null;
        if (this.performanceObserver) {
            this.performanceObserver.disconnect();
            this.performanceObserver = null;
        }
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }

        logger.debug('📄 NetworkObserverStrategy: cleaned up');
    }
}
